export type SocketDirection = 'in' | 'out'

export interface SocketBufferEntry {
  timestamp: number
  namespace: string
  direction: SocketDirection
  event: string
  payload: any
}

interface BufferOptions {
  maxAgeMs?: number
  maxEntries?: number
  sensitiveKeys?: string[]
  maxPayloadChars?: number
}

const DEFAULT_SENSITIVE_KEYS = [
  'token',
  'accessToken',
  'refreshToken',
  'password',
  'authorization',
  'apiKey',
  'secret',
  'cpf',
  'cnpj',
  'phone',
  'whatsappPhone',
]

class SocketBreadcrumbBuffer {
  private entries: SocketBufferEntry[] = []
  private readonly maxAgeMs: number
  private readonly maxEntries: number
  private readonly sensitiveKeys: Set<string>
  private readonly maxPayloadChars: number

  constructor(options: BufferOptions = {}) {
    this.maxAgeMs = options.maxAgeMs ?? 60_000
    this.maxEntries = options.maxEntries ?? 200
    this.maxPayloadChars = options.maxPayloadChars ?? 2_000
    this.sensitiveKeys = new Set(
      (options.sensitiveKeys ?? DEFAULT_SENSITIVE_KEYS).map((k) => k.toLowerCase())
    )
  }

  push(entry: Omit<SocketBufferEntry, 'timestamp'> & { timestamp?: number }): void {
    const now = entry.timestamp ?? Date.now()
    this.entries.push({
      timestamp: now,
      namespace: entry.namespace,
      direction: entry.direction,
      event: entry.event,
      payload: this.sanitize(entry.payload),
    })
    this.evict(now)
  }

  snapshot(): SocketBufferEntry[] {
    this.evict(Date.now())
    return this.entries.slice()
  }

  clear(): void {
    this.entries = []
  }

  private evict(now: number): void {
    const cutoff = now - this.maxAgeMs
    while (this.entries.length > 0 && this.entries[0].timestamp < cutoff) {
      this.entries.shift()
    }
    while (this.entries.length > this.maxEntries) {
      this.entries.shift()
    }
  }

  private sanitize(value: any, depth = 0): any {
    if (value === null || value === undefined) return value
    if (depth > 4) return '[Truncated:depth]'

    if (typeof value === 'string') {
      return value.length > this.maxPayloadChars
        ? value.slice(0, this.maxPayloadChars) + '…[truncated]'
        : value
    }
    if (typeof value === 'number' || typeof value === 'boolean') return value

    if (Array.isArray(value)) {
      const limit = 50
      const out = value.slice(0, limit).map((v) => this.sanitize(v, depth + 1))
      if (value.length > limit) out.push(`…[+${value.length - limit} items]`)
      return out
    }

    if (typeof value === 'object') {
      const out: Record<string, any> = {}
      for (const key of Object.keys(value)) {
        if (this.sensitiveKeys.has(key.toLowerCase())) {
          out[key] = '[Filtered]'
        } else {
          out[key] = this.sanitize(value[key], depth + 1)
        }
      }
      return out
    }

    return String(value)
  }
}

export const socketBuffer = new SocketBreadcrumbBuffer()
export default SocketBreadcrumbBuffer
